import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SeoHead from "../components/seo/SeoHead";
import API from "../services/api";

const THEME_KEY = "hookora_theme";

function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("We’re verifying your email now...");
  const [verifiedUser, setVerifiedUser] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        setStatus("error");
        setMessage("Verification link is missing a token. Please request a new verification email.");
        return;
      }

      try {
        const response = await API.post("/users/verify-email", { token });
        const responseData = response?.data || {};

        setVerifiedUser(responseData.user || null);
        setStatus("success");
        setMessage(
          responseData.message || "Your email has been verified successfully."
        );
      } catch (err) {
        console.error("Email verification failed:", err?.response?.data || err);
        setStatus("error");
        setMessage(
          err?.response?.data?.detail ||
            "This verification link is invalid or has expired."
        );
      }
    };

    verifyEmail();
  }, [location.search]);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(34,197,94,0.10), transparent 26%), radial-gradient(circle at top right, rgba(59,130,246,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      divider: isDark ? "rgba(148,163,184,0.14)" : "#f1f5f9",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      badgeBg: isDark ? "rgba(5,150,105,0.18)" : "#ecfdf5",
      badgeText: isDark ? "#86efac" : "#047857",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      infoBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      infoBorder: isDark ? "rgba(34,211,238,0.30)" : "#a5f3fc",
      infoText: isDark ? "#a5f3fc" : "#155e75",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 14px 40px rgba(15, 23, 42, 0.06)",
      primaryButtonShadow: isDark
        ? "0 12px 26px rgba(37,99,235,0.20)"
        : "0 10px 24px rgba(37,99,235,0.22)",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      accentLink: isDark ? "#93c5fd" : "#2563eb",
    }),
    [isDark]
  );

  const statusBoxStyle =
    status === "success"
      ? {
          color: colors.successText,
          background: colors.successBg,
          border: `1px solid ${colors.successBorder}`,
        }
      : status === "error"
      ? {
          color: colors.errorText,
          background: colors.errorBg,
          border: `1px solid ${colors.errorBorder}`,
        }
      : {
          color: colors.infoText,
          background: colors.infoBg,
          border: `1px solid ${colors.infoBorder}`,
        };

  return (
    <div
      style={{
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "100vh",
      }}
    >
      <SeoHead
        title="Verify Email | Hookora"
        description="Verify your Hookora email address to activate your account properly and complete referral eligibility."
        path="/verify-email"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "560px",
          margin: "48px auto",
          padding: "16px",
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "24px",
            padding: "32px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "7px 12px",
                borderRadius: "999px",
                background: colors.badgeBg,
                color: colors.badgeText,
                fontSize: "12px",
                fontWeight: "700",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              Email verification
            </div>

            <h1
              style={{
                marginTop: 0,
                marginBottom: "10px",
                color: colors.text,
                fontSize: "32px",
                lineHeight: "1.1",
              }}
            >
              Verify your email
            </h1>

            <p
              style={{
                color: colors.textMuted,
                marginBottom: 0,
                lineHeight: "1.6",
                fontSize: "15px",
              }}
            >
              We’re confirming your email so your Hookora account and referral eligibility are properly activated.
            </p>
          </div>

          <div
            style={{
              ...statusBoxStyle,
              padding: "14px",
              borderRadius: "14px",
              lineHeight: "1.6",
              marginBottom: "18px",
            }}
          >
            {message}
          </div>

          {status === "success" && (
            <>
              <div
                style={{
                  background: colors.infoBg,
                  border: `1px solid ${colors.infoBorder}`,
                  color: colors.infoText,
                  borderRadius: "14px",
                  padding: "14px",
                  lineHeight: "1.7",
                  marginBottom: "18px",
                  fontSize: "14px",
                }}
              >
                {verifiedUser?.email ? (
                  <>
                    Verified email: <strong>{verifiedUser.email}</strong>
                    <br />
                  </>
                ) : null}
                Your account is ready. You can now log in and continue using Hookora.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexDirection: "column",
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "700",
                    fontSize: "15px",
                    boxShadow: colors.primaryButtonShadow,
                  }}
                >
                  Go to Login
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background: colors.secondaryButtonBg,
                    color: colors.secondaryButtonText,
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "700",
                    fontSize: "15px",
                  }}
                >
                  Back to Register
                </button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div
                style={{
                  color: colors.textSoft,
                  lineHeight: "1.7",
                  fontSize: "14px",
                  marginBottom: "18px",
                }}
              >
                You can request a new verification email from the registration page or try signing up again if needed.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexDirection: "column",
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background: "linear-gradient(135deg, #16a34a, #059669)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "700",
                    fontSize: "15px",
                  }}
                >
                  Back to Register
                </button>

                <Link
                  to="/login"
                  style={{
                    textAlign: "center",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    background: colors.secondaryButtonBg,
                    color: colors.secondaryButtonText,
                    textDecoration: "none",
                    fontWeight: "700",
                    fontSize: "15px",
                  }}
                >
                  Go to Login
                </Link>
              </div>
            </>
          )}

          {status === "verifying" && (
            <div
              style={{
                color: colors.textSoft,
                lineHeight: "1.7",
                fontSize: "14px",
              }}
            >
              This usually takes just a moment. Please keep this page open while we confirm your verification token.
            </div>
          )}

          <div
            style={{
              marginTop: "20px",
              paddingTop: "18px",
              borderTop: `1px solid ${colors.divider}`,
            }}
          >
            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                lineHeight: "1.6",
                fontSize: "14px",
              }}
            >
              Need help?{" "}
              <Link
                to="/contact-support"
                style={{
                  color: colors.accentLink,
                  fontWeight: "700",
                  textDecoration: "none",
                }}
              >
                Contact support
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default VerifyEmailPage;